import { materialsFor, defaultRegularMaterial, resolveRegularMaterial, regularMaterialLabel, regularMaterialDisplayName } from './regular-materials-20261025.js';

const copy = {
  en: { all: 'All styles', style: 'Style', colour: 'Colour & style', selected: 'Selected fabric', choose: 'Choose a fabric colour', sample: 'Catalogue fabric sample', view: 'View fabric detail', included: 'White aluminum-alloy cassette included', empty: 'No fabric colours are available for this selection.', white: 'White default', milan: 'Milan: chain or motorized · Max height 72 in', illustration: 'White installation illustration', illustrationNote: 'White fabric shown for installation and light-control reference. See the selected fabric sample for your colour.' },
  fr: { all: 'Tous les styles', style: 'Style', colour: 'Couleur et style', selected: 'Tissu choisi', choose: 'Choisissez une couleur de tissu', sample: 'Échantillon du catalogue', view: 'Voir le tissu', included: 'Boîtier blanc en alliage d’aluminium inclus', empty: 'Aucune couleur de tissu n’est disponible pour cette sélection.', white: 'Blanc par défaut', milan: 'Milan : chaînette ou motorisé · Hauteur max. 72 po', illustration: 'Illustration de pose en blanc', illustrationNote: 'Le tissu blanc illustre la pose et le contrôle de la lumière. Consultez l’échantillon choisi pour votre couleur.' },
  zh: { all: '全部款式', style: '款式', colour: '颜色与款式', selected: '已选面料', choose: '请选择面料颜色', sample: '目录原始面料样品', view: '查看面料细节', included: '包含白色铝合金帘盒', empty: '当前选择暂无可用面料颜色。', white: '默认白色', milan: 'Milan：拉珠或电动 · 高度最高72英寸', illustration: '白色安装示意图', illustrationNote: '图中白色面料仅用于展示安装和采光效果，所选颜色请参看面料样品。' },
};

const localeFor = language => language?.toLowerCase().startsWith('zh') ? 'zh' : language?.toLowerCase().startsWith('fr') ? 'fr' : 'en';
const materialName = regularMaterialDisplayName;

export class RegularMaterialPickerModel extends EventTarget {
  constructor(initial = {}) {
    super();
    this.state = { family: 'zebra', type: 'light_filtering', materialId: '', lang: 'en', pending: false };
    this.style = null;
    this.sync(initial);
  }

  get view() {
    const fallback = defaultRegularMaterial(this.state.family, this.state.type);
    const options = [...materialsFor(this.state.family, this.state.type)].sort((a, b) => {
      if (a.id === b.id) return 0;
      if (a.id === fallback?.id) return -1;
      if (b.id === fallback?.id) return 1;
      return Number(Boolean(b.isWhite)) - Number(Boolean(a.isWhite));
    });
    return {
      ...this.state,
      material: resolveRegularMaterial(this.state.family, this.state.type, this.state.materialId),
      options,
      styles: [...new Set(options.map(material => material.style))],
      style: this.style,
      visible: this.style ? options.filter(material => material.style === this.style) : options,
    };
  }

  sync(next = {}) {
    const materialChanged = Object.hasOwn(next, 'materialId') && next.materialId !== this.state.materialId;
    if ((next.family && next.family !== this.state.family) || (next.type && next.type !== this.state.type)) this.style = null;
    this.state = { ...this.state, ...next, lang: localeFor(next.lang || this.state.lang) };
    if (!this.state.materialId) this.state.materialId = defaultRegularMaterial(this.state.family, this.state.type)?.id || '';
    const selected = resolveRegularMaterial(this.state.family, this.state.type, this.state.materialId);
    if (this.style === null || (materialChanged && this.style && selected && selected.style !== this.style)) this.style = selected?.style || '';
    if (this.style && !materialsFor(this.state.family, this.state.type).some(material => material.style === this.style)) this.style = selected?.style || '';
    this.dispatchEvent(new Event('update'));
  }

  filter(style) {
    if (this.state.pending) return;
    this.style = this.view.styles.includes(style) ? style : '';
    const view = this.view;
    if (this.style && view.material?.style !== this.style && view.visible.length) this.select(view.visible[0].id);
    else this.dispatchEvent(new Event('update'));
  }

  select(materialId) {
    if (this.state.pending || !materialsFor(this.state.family, this.state.type).some(material => material.id === materialId)) return false;
    if (materialId === this.state.materialId) return false;
    this.state.materialId = materialId;
    const selected = resolveRegularMaterial(this.state.family, this.state.type, materialId);
    if (this.style && selected?.style !== this.style) this.style = selected.style;
    this.dispatchEvent(new Event('update'));
    this.dispatchEvent(new CustomEvent('change', { detail: { materialId } }));
    return true;
  }
}

const bindings = new WeakMap();
function bindPicker(component) {
  if (bindings.has(component)) return;
  const configurator = component.closest('.configurator');
  const family = configurator?.querySelector('#product-family');
  const filter = component.querySelector('[data-regular-material-style]');
  const grid = component.querySelector('[data-regular-material-options]');
  const selectedName = component.querySelector('[data-regular-material-name]');
  const sample = component.querySelector('[data-regular-material-sample]');
  const sampleButton = component.querySelector('[data-regular-material-view]');
  const fieldset = component.querySelector('[data-regular-material-fieldset]');
  if (!configurator || !family || !filter || !grid || !selectedName || !sample || !sampleButton || !fieldset) return;
  const type = () => configurator.querySelector('.type-option[aria-pressed="true"]')?.dataset.lightType === 'opaque' ? 'opaque' : 'light_filtering';
  const model = new RegularMaterialPickerModel({ family: family.value, type: type(), materialId: configurator.dataset.regularMaterialId || '', lang: document.documentElement.lang });
  let cardsKey = '';
  let stylesKey = '';
  const render = () => {
    const view = model.view;
    const text = copy[view.lang];
    configurator.dataset.regularMaterialId = view.materialId || '';
    fieldset.disabled = Boolean(view.pending);
    filter.disabled = Boolean(view.pending);
    component.setAttribute('aria-busy', String(Boolean(view.pending)));
    for (const node of component.querySelectorAll('[data-regular-material-copy]')) node.textContent = text[node.dataset.regularMaterialCopy] || '';
    const nextStylesKey = JSON.stringify([view.lang, view.styles]);
    if (nextStylesKey !== stylesKey) {
      const all = new Option(text.all, '');
      filter.replaceChildren(all, ...view.styles.map(style => new Option(style, style)));
      stylesKey = nextStylesKey;
    }
    filter.value = view.style;
    const nextCardsKey = JSON.stringify([view.lang, view.visible.map(material => material.id)]);
    if (nextCardsKey !== cardsKey) {
      const cards = view.visible.map(material => {
        const card = document.createElement('label');
        card.className = 'regular-material-card';
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = `regular-material-${component.dataset.regularMaterialPicker}`;
        radio.value = material.id;
        radio.className = 'regular-material-radio';
        radio.setAttribute('aria-label', materialName(material, view.lang));
        const content = document.createElement('span');
        content.className = 'regular-material-card-content';
        const image = document.createElement('img');
        image.src = material.thumbnail || material.image;
        image.alt = '';
        image.width = 64;
        image.height = 64;
        image.loading = 'lazy';
        image.decoding = 'async';
        const label = document.createElement('span');
        label.className = 'regular-material-card-label';
        label.textContent = regularMaterialLabel(material, view.lang);
        const code = document.createElement('span');
        code.className = 'regular-material-card-code';
        code.textContent = material.style;
        const check = document.createElement('span');
        check.className = 'regular-material-check';
        check.setAttribute('aria-hidden', 'true');
        check.textContent = '✓';
        content.append(image, label, code, check);
        card.append(radio, content);
        return card;
      });
      grid.replaceChildren(...cards);
      cardsKey = nextCardsKey;
    }
    for (const radio of grid.querySelectorAll('input[type="radio"]')) radio.checked = radio.value === view.material?.id;
    const empty = component.querySelector('[data-regular-material-empty]');
    if (empty) empty.hidden = view.visible.length > 0;
    selectedName.textContent = view.material ? materialName(view.material, view.lang) : text.choose;
    const eligibilityNote = component.querySelector('[data-regular-material-eligibility]');
    if (eligibilityNote) eligibilityNote.hidden = view.material?.style !== 'Milan';
    sampleButton.disabled = !view.material;
    sample.hidden = !view.material;
    if (view.material) {
      const src = view.material.thumbnail || view.material.image;
      if (sample.getAttribute('src') !== src) sample.setAttribute('src', src);
      sample.alt = `${materialName(view.material, view.lang)} · ${text.sample}`;
      sampleButton.setAttribute('aria-label', `${text.view} · ${materialName(view.material, view.lang)}`);
    }

  };
  const stopNativeInput = event => event.stopPropagation();
  const change = event => {
    event.stopPropagation();
    const radio = event.target.closest?.('.regular-material-radio');
    if (radio?.checked) model.select(radio.value);
  };
  const filterChange = () => model.filter(filter.value);
  const emit = event => component.dispatchEvent(new CustomEvent('evereno:regular-material-change', { bubbles: true, detail: event.detail }));
  const sync = event => {
    if (!event.detail) return;
    model.sync(event.detail);
  };
  const language = () => model.sync({ lang: document.documentElement.lang });
  const observer = new MutationObserver(language);
  observer.observe(document.documentElement, { attributes: true, attributeFilter: ['lang'] });
  model.addEventListener('update', render);
  model.addEventListener('change', emit);
  component.addEventListener('input', stopNativeInput);
  component.addEventListener('change', change);
  filter.addEventListener('change', filterChange);
  document.addEventListener('evereno:regular-material-sync', sync);
  const dispose = event => {
    if (!event.target.contains(component)) return;
    observer.disconnect();
    model.removeEventListener('update', render);
    model.removeEventListener('change', emit);
    component.removeEventListener('input', stopNativeInput);
    component.removeEventListener('change', change);
    filter.removeEventListener('change', filterChange);
    document.removeEventListener('evereno:regular-material-sync', sync);
    document.removeEventListener('shopify:section:unload', dispose);
    bindings.delete(component);
  };
  document.addEventListener('shopify:section:unload', dispose);
  bindings.set(component, model);
  render();
  document.dispatchEvent(new CustomEvent('evereno:regular-material-ready'));
}

function initialize(scope = document) {
  if (scope.matches?.('[data-regular-material-picker]')) bindPicker(scope);
  scope.querySelectorAll('[data-regular-material-picker]').forEach(bindPicker);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
}
